import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { motion } from "framer-motion";
import "./ContactSupport.css";
import { useSearchParams, useNavigate } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaw,
  faPaperPlane,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";

import { Storage } from "aws-amplify";

const API_BASE = process.env.REACT_APP_API_BASE;

// 🔎 Simple keyword-based classifier
function classifyTopic(text) {
  const t = (text || "").toLowerCase();

  if (
    t.includes("bill") ||
    t.includes("charge") ||
    t.includes("payment") ||
    t.includes("subscription") ||
    t.includes("refund") ||
    t.includes("invoice") ||
    t.includes("plan")
  ) {
    return "Billing / Subscription";
  }

  if (
    t.includes("lost tag") ||
    t.includes("lost my tag") ||
    t.includes("lost dog") ||
    t.includes("lost cat") ||
    t.includes("missing tag") ||
    t.includes("found a pet") ||
    t.includes("found dog") ||
    t.includes("found cat")
  ) {
    return "Lost or Found Pet / Tag";
  }

  if (
    t.includes("error") ||
    t.includes("bug") ||
    t.includes("not working") ||
    t.includes("issue") ||
    t.includes("problem") ||
    t.includes("can’t log in") ||
    t.includes("cant log in") ||
    t.includes("login") ||
    t.includes("sign in")
  ) {
    return "Technical Support";
  }

  if (
    t.includes("idea") ||
    t.includes("feature") ||
    t.includes("suggestion") ||
    t.includes("feedback")
  ) {
    return "Feedback / Feature Request";
  }

  return "General Question";
}

const STEPS = {
  ASK_NAME: "ASK_NAME",
  ASK_CONTACT: "ASK_CONTACT",
  ASK_TAG: "ASK_TAG",
  ASK_PROBLEM: "ASK_PROBLEM",
  CONFIRM: "CONFIRM",
  DONE: "DONE",
};

// ⏱ Cooldowns
const MESSAGE_COOLDOWN_MS = 700;
const THROTTLE_WARNING_WINDOW_MS = 3000;
const TICKET_COOLDOWN_MS = 2 * 60 * 1000;

// 🧹 Simple banned/offensive words list
const BANNED_WORDS = [
  "fuck",
  "shit",
  "bitch",
  "asshole",
  "kill yourself",
  "kys",
  "die",
  "hate you",
];

// --- Smart extract helpers ---
function extractNameFromText(text) {
  if (!text) return null;

  const nameMatch1 = text.match(/my name is\s+([A-Za-z][A-Za-z\s'-]{1,40})/i);
  if (nameMatch1) {
    const candidate = nameMatch1[1].split(/[.,!]/)[0].trim();
    return candidate || null;
  }

  const nameMatch2 = text.match(
    /\b(i am|i'm)\s+([A-Za-z][A-Za-z\s'-]{1,40})/i
  );
  if (nameMatch2) {
    const candidate = nameMatch2[2].split(/[.,!]/)[0].trim();
    return candidate || null;
  }

  return null;
}

function extractContactFromText(text) {
  if (!text) return null;

  const emailMatch = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  if (emailMatch) return emailMatch[0];

  const phoneMatch = text.match(/(\+?\d[\d\-\s().]{6,}\d)/);
  if (phoneMatch) return phoneMatch[0].trim();

  return null;
}

function extractSmartTagFromText(text) {
  if (!text) return null;
  const match = text.match(/LP\d{3,}/i);
  return match ? match[0].toUpperCase() : null;
}

function extractProblemFromText(text, name, contact, smartTagId) {
  if (!text) return null;

  let remaining = text;

  if (name) {
    remaining = remaining.replace(new RegExp(name, "i"), "");
    remaining = remaining.replace(/my name is/i, "");
  }
  if (contact) remaining = remaining.replace(contact, "");
  if (smartTagId) remaining = remaining.replace(new RegExp(smartTagId, "i"), "");

  remaining = remaining.replace(/\s+/g, " ").trim();

  if (remaining.length < 8) remaining = text.trim();

  return remaining;
}

// 🧹 Remove emojis & symbols that sound awkward in TTS
const stripEmojis = (text = "") =>
  String(text)
    .replace(
      /[\p{Extended_Pictographic}\p{Emoji_Presentation}\p{Emoji}\u200d]/gu,
      ""
    )
    .replace(/\s+/g, " ")
    .trim();

export default function ContactSupport() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // found=1 means "found pet" / finder flow
  const isFoundFlow = searchParams.get("found") === "1";
  const isFinderFlow = isFoundFlow;
  const tagFromQuery = (searchParams.get("tag") || "").toUpperCase();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState(
    isFoundFlow ? "Lost or Found Pet / Tag" : null
  );
  const [step, setStep] = useState(STEPS.ASK_NAME);
  const [isFlagged, setIsFlagged] = useState(false);
  const [assistantThinking, setAssistantThinking] = useState(false);
  const [petPhotoUrl, setPetPhotoUrl] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoError, setPhotoError] = useState("");

  const [form, setForm] = useState({
    name: "",
    contact: "",
    smartTagId: tagFromQuery || "",
    problem: "",
  });

  // ✅ Polly Voice
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const lastSpokenAiIndexRef = useRef(-1);

  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);

  const audioQueueRef = useRef([]); // holds base64 strings
  const isPlayingRef = useRef(false); // playback lock
  const voicePrimedRef = useRef(false);
const gesturePrimedOnceRef = useRef(false);
const primeAudioB64Ref = useRef(null);

  // ✅ volume override for priming


  // ✅ 1) messagesRef: always latest messages inside async funcs
  const messagesRef = useRef([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ✅ your Lambda URL (Polly-enabled)
  const PAW_ASSISTANT_URL =
    "https://tiwqg7ei5lnwvl2o3oudz26z7q0vzzwc.lambda-url.us-east-1.on.aws/";

  const stopVoice = () => {
    try {
      audioQueueRef.current = [];
      isPlayingRef.current = false;

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    } catch {}
  };

  const playNextFromQueue = () => {
    if (isPlayingRef.current) return;
    const item = audioQueueRef.current.shift();
if (!item?.b64) return;
const nextBase64 = item.b64;
const vol = typeof item.vol === "number" ? item.vol : 1;

    if (!nextBase64) return;

    isPlayingRef.current = true;

    try {
      // stop/revoke any previous url cleanly
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }

      const bytes = Uint8Array.from(atob(nextBase64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;

      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = url;

      // ✅ respect prime volume (0) for ONLY the next clip
      audioRef.current.volume = vol;


      audioRef.current.onended = () => {
        try {
          if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
          }
        } catch {}
        isPlayingRef.current = false;
        playNextFromQueue(); // ✅ keep going
      };

      audioRef.current
  .play()

  .catch((e) => {
    console.warn("[Polly] play blocked ❌", e?.name, e?.message);
    isPlayingRef.current = false;
    playNextFromQueue();
  });
    } catch (e) {
      console.warn("[Polly] queue play error:", e);
      isPlayingRef.current = false;
      playNextFromQueue();
    }
  };

  // enqueue instead of interrupting
 const playPollyBase64 = (audioBase64, vol = 1) => {
  if (!audioBase64) return;
  audioQueueRef.current.push({ b64: audioBase64, vol });
  playNextFromQueue();
};


  const fetchPollyForText = async (text) => {
    try {
      const res = await fetch(PAW_ASSISTANT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "tts", userMessage: text, voice: true }),
      });

      const raw = await res.text().catch(() => "");
      if (!res.ok) {
        console.warn("[Polly] TTS error:", res.status, raw?.slice(0, 200));
        return null;
      }

      let data = {};
      try {
        data = JSON.parse(raw || "{}");
      } catch {}

      return data.audioBase64 || null;
    } catch (e) {
      console.warn("[Polly] fetch failed:", e);
      return null;
    }
  };

  // ✅ 2) speak ALL pending AI messages in order
  const speakPendingAiMessages = async () => {
    if (!voiceEnabled) return;
    if (!voicePrimedRef.current) return;

    const list = messagesRef.current || [];
    if (!list.length) return;

    // find all AI messages
    const aiIndexes = [];
    for (let i = 0; i < list.length; i++) {
      if (list[i]?.sender === "ai") aiIndexes.push(i);
    }
    if (!aiIndexes.length) return;

    // speak any AI messages after the last spoken index
    const start = lastSpokenAiIndexRef.current;
    const pending = aiIndexes.filter((i) => i > start);
    if (!pending.length) return;

    // mark the last one as spoken now (prevents double speaking)
    lastSpokenAiIndexRef.current = pending[pending.length - 1];

    // enqueue each message in order (queue prevents cutoffs)
    for (const idx of pending) {
      const cleanText = stripEmojis(list[idx]?.text || "");
      if (!cleanText) continue;

      try {
        const audioBase64 = await fetchPollyForText(cleanText);
        if (audioBase64) playPollyBase64(audioBase64, 1);

      } catch {}
    }
  };
useEffect(() => {
  // preload prime audio (no playback here)
  (async () => {
    try {
      const b64 = await fetchPollyForText(stripEmojis("Voice enabled."));
      if (b64) primeAudioB64Ref.current = b64;
    } catch {}
  })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // ✅ 4) primeVoice triggers pending speaker after it primes
  // Key: no `if (!voiceEnabled) return;` so it works during toggle timing
const primeVoice = async () => {
  if (voicePrimedRef.current) return;

  try {
    let b64 = primeAudioB64Ref.current;

    // fallback if not cached yet
    if (!b64) b64 = await fetchPollyForText(stripEmojis("Voice enabled."));
    if (!b64) return;

    playPollyBase64(b64, 0); // silent
    voicePrimedRef.current = true;

    setTimeout(() => speakPendingAiMessages(), 250);
  } catch (e) {
    console.warn("[Polly] prime failed:", e);
  }
};

useEffect(() => {
  if (!voiceEnabled) return;

  const onFirstGesture = () => {
    if (voicePrimedRef.current) return;
    if (gesturePrimedOnceRef.current) return;

    gesturePrimedOnceRef.current = true;

    // reset tracking
    lastSpokenAiIndexRef.current = -1;

    // IMPORTANT: don't await — keep it in gesture stack as much as possible
    primeVoice();

    // catch up shortly after
    setTimeout(() => {
      speakPendingAiMessages();
    }, 300);
  };

  window.addEventListener("pointerdown", onFirstGesture, true);
  window.addEventListener("keydown", onFirstGesture, true);

  return () => {
    window.removeEventListener("pointerdown", onFirstGesture, true);
    window.removeEventListener("keydown", onFirstGesture, true);
  };
}, [voiceEnabled]);


  // ✅ 3) Speak effect: speak pending messages (not just latest)
  useEffect(() => {
    if (!voiceEnabled) return;
    if (!voicePrimedRef.current) return;
    speakPendingAiMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, voiceEnabled]);

  const lastMessageTimeRef = useRef(0);
  const lastThrottleWarnRef = useRef(0);
  const lastTicketCreatedAtRef = useRef(null);

  const addMessage = (sender, text) =>
    setMessages((prev) => [...prev, { sender, text }]);

  // ✅ Back to normal (no Polly here)
  const addAiMessageWithDelay = (text) => {
    const perChar = 65;
    const minDelay = 1200;
    const maxDelay = 5000;

    const delay = Math.max(minDelay, Math.min(maxDelay, text.length * perChar));

    setAssistantThinking(true);

    setTimeout(() => {
      setMessages((prev) => [...prev, { sender: "ai", text }]);
      setAssistantThinking(false);
    }, delay);
  };

  const chatEndRef = useRef(null);
  const didInitRef = useRef(false);

  const scrollToBottom = (behavior = "auto") => {
    if (!chatEndRef.current) return;
    chatEndRef.current.scrollIntoView({
      behavior,
      block: "end",
    });
  };

  useEffect(() => {
    scrollToBottom("auto");
  }, [messages.length, loading, assistantThinking]);

  // Initial greeting
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const baseGreeting = "Hi! I'm Paw, the Little Paws Support Assistant 🐾";

    const foundGreeting =
      isFoundFlow && tagFromQuery
        ? `${baseGreeting}\n\nIt looks like you're reporting a **found pet** with SmartTag **${tagFromQuery}**. I'll collect a few details so we can safely notify the owner.`
        : `${baseGreeting}\n\nI'll help you create a support ticket so our team can follow up.`;

    const perChar = 55;
    const minDelay = 900;
    const maxDelay = 4000;
    const greetingDelay = Math.max(
      minDelay,
      Math.min(maxDelay, foundGreeting.length * perChar)
    );

    addAiMessageWithDelay(foundGreeting);

    setTimeout(() => {
      addAiMessageWithDelay("First, what's your *name*?");
    }, greetingDelay + 800);
  }, [isFoundFlow, tagFromQuery]);

  // 🐾 Load pet photo for /found?found=1&tag=LP####
  useEffect(() => {
    if (!isFoundFlow || !tagFromQuery) return;

    let cancelled = false;

    const loadPetPhoto = async () => {
      try {
        setPhotoError("");
        setPetPhotoUrl(null);
        setPhotoLoading(true);

        // public: pets/LP####/...
        const listResult = await Storage.list(`pets/${tagFromQuery}/`, {
          level: "public",
        });
        const items = listResult?.results || listResult || [];
        if (!items.length || cancelled) return;

        const sorted = [...items].sort((a, b) => {
          const aTime = new Date(a.lastModified || 0).getTime();
          const bTime = new Date(b.lastModified || 0).getTime();
          return bTime - aTime; // newest first
        });

        const latest = sorted[0];
        const url = await Storage.get(latest.key, { level: "public" });
        if (!cancelled) setPetPhotoUrl(url);
      } catch (err) {
        console.error("ContactSupport – pet photo load error:", err);
        if (!cancelled) setPhotoError("We couldn't load the pet photo yet.");
      } finally {
        if (!cancelled) setPhotoLoading(false);
      }
    };

    loadPetPhoto();

    return () => {
      cancelled = true;
    };
  }, [isFoundFlow, tagFromQuery]);

  const resetFlow = () => {
    setForm({
      name: "",
      contact: "",
      smartTagId: isFinderFlow ? tagFromQuery || "" : "",
      problem: "",
    });

    setTopic(isFinderFlow ? "Lost or Found Pet / Tag" : null);
    setStep(STEPS.ASK_NAME);
    setMessages([]);
    lastSpokenAiIndexRef.current = -1;

    const intro = isFinderFlow
      ? "No problem, let's start a new *found pet* ticket. 🐾\n\nFirst, what's your *name*?"
      : "No problem, let's start a new ticket. 🐾\n\nFirst, what's your *name*?";

    addAiMessageWithDelay(intro);
  };

  async function createSupportTicket(payload) {
    const res = await fetch(`${API_BASE}/support/tickets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(`Ticket create failed: ${res.status}`);

    return res.json(); // { ticketId, status }
  }

  const handleSend = async () => {
    if (!input.trim() || loading || assistantThinking) return;

    if (isFlagged) {
      addAiMessageWithDelay(
        "Your request was flagged. Please refresh the page and try again."
      );
      setInput("");
      return;
    }


    const now = Date.now();

    // Throttle
    const sinceLast = now - lastMessageTimeRef.current;
    if (sinceLast < MESSAGE_COOLDOWN_MS) {
      const sinceWarn = now - lastThrottleWarnRef.current;
      if (sinceWarn > THROTTLE_WARNING_WINDOW_MS) {
        addAiMessageWithDelay("Slow down just a bit so I can keep up 🐾");
        lastThrottleWarnRef.current = now;
      }
      setInput("");
      return;
    }
    lastMessageTimeRef.current = now;

    const userMessage = input.trim();
    const lowerMsg = userMessage.toLowerCase();
    setInput("");

    if (BANNED_WORDS.some((w) => lowerMsg.includes(w))) {
      addAiMessageWithDelay(
        "Please keep messages respectful so I can help you properly 🐾"
      );
      return;
    }

    const lastMsg = messages.slice().reverse().find((m) => m.sender === "user");
    if (lastMsg && lastMsg.text === userMessage) {
      addAiMessageWithDelay(
        "Looks like you already said that. Try rephrasing or adding more details 😊"
      );
      return;
    }

    addMessage("user", userMessage);

    if (step === STEPS.DONE) {
      if (lowerMsg.includes("new")) {
        resetFlow();
      } else {
        addAiMessageWithDelay(
          "Your ticket is already created ✅.\nIf you need a *new* ticket, just type “new ticket”."
        );
      }
      return;
    }

    // State machine
    switch (step) {
      case STEPS.ASK_NAME: {
        const guessedName = extractNameFromText(userMessage);
        const guessedContact = extractContactFromText(userMessage);
        const guessedTag = extractSmartTagFromText(userMessage);
        const guessedProblem = extractProblemFromText(
          userMessage,
          guessedName,
          guessedContact,
          guessedTag
        );

        if (guessedName || guessedContact || guessedProblem || guessedTag) {
          const updatedForm = {
            name: guessedName || userMessage,
            contact: guessedContact || "",
            smartTagId: guessedTag || "",
            problem: guessedProblem || "",
          };

          const detectedTopic = updatedForm.problem
            ? classifyTopic(updatedForm.problem)
            : null;

          setForm(updatedForm);
          if (detectedTopic) setTopic(detectedTopic);

          if (updatedForm.name && updatedForm.contact && updatedForm.problem) {
            const summaryLines = [
              `Here's what I'll send to the Little Paws team:`,
              "",
              `• Name: ${updatedForm.name}`,
              `• Contact: ${updatedForm.contact}`,
              `• SmartTag ID: ${updatedForm.smartTagId || "None"}`,
              `• Topic: ${detectedTopic || "General Question"}`,
              `• Problem: ${updatedForm.problem}`,
              "",
              `Does this look correct? Reply “yes” to create the ticket or “no” to start over.`,
            ].join("\n");

            addAiMessageWithDelay(summaryLines);
            setStep(STEPS.CONFIRM);
            break;
          }

          if (updatedForm.name && !updatedForm.contact && updatedForm.problem) {
            addAiMessageWithDelay(
              `Nice to meet you, ${updatedForm.name}! 😊\n\nWhat's the best *email or phone number* to reach you?`
            );
            setStep(STEPS.ASK_CONTACT);
            break;
          }

          if (updatedForm.name && updatedForm.contact && !updatedForm.problem) {
            addAiMessageWithDelay(
              "Got it, thanks! 📱\n\nIf you have a Little Paws SmartTag, what's the *SmartTag ID* (like LP####)? If you don't have one, just type “none”."
            );
            setStep(STEPS.ASK_TAG);
            break;
          }

          if (updatedForm.name && !updatedForm.contact && !updatedForm.problem) {
            setForm((prev) => ({ ...prev, name: updatedForm.name }));
            addAiMessageWithDelay(
              `Nice to meet you, ${updatedForm.name}! 😊\n\nWhat's the best *email or phone number* to reach you?`
            );
            setStep(STEPS.ASK_CONTACT);
            break;
          }

          setForm((prev) => ({ ...prev, name: userMessage }));
          addAiMessageWithDelay(
            `Nice to meet you, ${userMessage}! 😊\n\nWhat's the best *email or phone number* to reach you?`
          );
          setStep(STEPS.ASK_CONTACT);
          break;
        }

        const name = userMessage;
        setForm((prev) => ({ ...prev, name }));
        addAiMessageWithDelay(
          `Nice to meet you, ${name}! 😊\n\nWhat's the best *email or phone number* to reach you?`
        );
        setStep(STEPS.ASK_CONTACT);
        break;
      }

      case STEPS.ASK_CONTACT: {
        const contact = userMessage;
        setForm((prev) => ({ ...prev, contact }));

        if (isFoundFlow && (form.smartTagId || tagFromQuery)) {
          setForm((prev) => ({
            ...prev,
            smartTagId: prev.smartTagId || tagFromQuery,
          }));
          addAiMessageWithDelay(
            "Got it, thanks! 🐶\n\nNow please tell me briefly what happened (for example, where you found the pet and if they’re safe with you) so we can notify the owner."
          );
          setStep(STEPS.ASK_PROBLEM);
        } else {
          addAiMessageWithDelay(
            "Got it, thanks! 📱\n\nIf you have a Little Paws SmartTag, what's the *SmartTag ID* (like LP####)? If you don't have one, just type “none”."
          );
          setStep(STEPS.ASK_TAG);
        }
        break;
      }

      case STEPS.ASK_TAG: {
        const smartTagId = lowerMsg === "none" ? "" : userMessage.toUpperCase();
        setForm((prev) => ({ ...prev, smartTagId }));

        const followup = isFinderFlow
          ? "Thanks! 🐶\n\nNow please tell me briefly what happened (e.g., where you found the pet, if they’re safe with you, etc.) so we can notify the owner."
          : "Thanks! 🐶\n\nNow please explain the *problem or question* in a sentence or two so I can tag it for the right team.";

        addAiMessageWithDelay(followup);
        setStep(STEPS.ASK_PROBLEM);
        break;
      }

      case STEPS.ASK_PROBLEM: {
        const problem = userMessage;

        const detectedTopic = isFoundFlow
          ? "Lost or Found Pet / Tag"
          : classifyTopic(problem);

        setForm((prev) => ({ ...prev, problem }));
        setTopic(detectedTopic);

        const { name, contact, smartTagId } = {
          ...form,
          problem,
        };

        const summary = [
          `Here's what I'll send to the Little Paws team:`,
          "",
          `• Name: ${name || "(not provided)"}`,
          `• Contact: ${contact || "(not provided)"}`,
          `• SmartTag ID: ${smartTagId || tagFromQuery || "None"}`,
          `• Topic: ${detectedTopic}`,
          `• Problem: ${problem}`,
          "",
          `Does this look correct? Reply “yes” to create the ticket or “no” to start over.`,
        ].join("\n");

        addAiMessageWithDelay(summary);
        setStep(STEPS.CONFIRM);
        break;
      }

      case STEPS.CONFIRM: {
        const answer = lowerMsg;

        if (answer.startsWith("y")) {
          const now = Date.now();
          if (
            lastTicketCreatedAtRef.current &&
            now - lastTicketCreatedAtRef.current < TICKET_COOLDOWN_MS
          ) {
            const secondsLeft = Math.ceil(
              (TICKET_COOLDOWN_MS - (now - lastTicketCreatedAtRef.current)) /
                1000
            );
            addAiMessageWithDelay(
              `You just created a ticket 🐾. Please wait about ${secondsLeft} seconds before creating another one.`
            );
            return;
          }

          setLoading(true);
          try {
            const payload = {
              ...form,
              smartTagId: form.smartTagId || tagFromQuery || "",
              problem: form.problem,
              topic:
                topic ||
                (isFoundFlow
                  ? "Lost or Found Pet / Tag"
                  : classifyTopic(form.problem)),
              source: isFoundFlow ? "found_pet" : "contact_support_ai",
            };

            const result = await createSupportTicket(payload);
            lastTicketCreatedAtRef.current = Date.now();

            addAiMessageWithDelay(
              `Got it — I’ve created a support ticket **${result.ticketId}** and tagged this as “${payload.topic}”. ✅\n\nWe’ll get back to you at **${form.contact}** as soon as we can. ❤️\n\nIf you need another ticket later, just type “new ticket”.`
            );
            setStep(STEPS.DONE);
          } catch (err) {
            console.error("Ticket create error:", err);
            addAiMessageWithDelay(
              "Hmm, something went wrong creating your ticket. 😔\nPlease try again in a moment."
            );
          } finally {
            setLoading(false);
          }
        } else if (answer.startsWith("n")) {
          resetFlow();
        } else {
          addAiMessageWithDelay(
            'Please reply with “yes” to submit this ticket or “no” to start over.'
          );
        }
        break;
      }

      default:
        break;
    }
  };

  return (
    <>
      {/* Shell and messages (scrolling area) */}
      <div className="contact-support-wrapper">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="support-shell"
        >
          {/* Header – show pet for /found, regular support otherwise */}
          <div className="support-header">


            <div className="support-header-left">
              {isFoundFlow ? (
                <>
                  <div className="smarttag-header-avatar">
                    {petPhotoUrl ? (
                      <img
                        src={petPhotoUrl}
                        alt="Pet photo"
                        className="smarttag-pet-avatar"
                      />
                    ) : (
                      <div className="smarttag-pet-avatar placeholder">
                        <FontAwesomeIcon icon={faPaw} />
                      </div>
                    )}
                  </div>

                  <div className="support-header-text">
                    <h4 className="support-title mb-0">
                      Found a pet wearing a SmartTag
                    </h4>
                    <p className="support-subtitle mb-0">
                      {tagFromQuery
                        ? `You’re helping us reach the family for SmartTag ${tagFromQuery}.`
                        : "You’re helping us reach this pet’s family."}
                    </p>

                    {tagFromQuery && (
                      <div className="smarttag-tag-row mt-1">
                        <span className="badge bg-secondary smarttag-tag-badge">
                          Tag: {tagFromQuery}
                        </span>
                      </div>
                    )}

                    {photoLoading && (
                      <div className="smarttag-tag-loading">(Loading photo…)</div>
                    )}
                    {photoError && (
                      <div className="smarttag-photo-error">{photoError}</div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="support-header-icon">
                    <FontAwesomeIcon icon={faPaw} />
                  </div>
                  <div className="support-header-text">
                    <h4 className="support-title mb-0">Paw Support Assistant</h4>
                    <p className="support-subtitle mb-0">
                      Contact our team using a simple, guided chat.
                    </p>
                  </div>
                </>
              )}
            </div>

            {topic && <span className="support-topic-pill">Topic: {topic}</span>}
          </div>

          <div className="chat-box support-chat-box">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`support-bubble ${
                  msg.sender === "ai" ? "ai-support" : "user-support"
                }`}
              >
                {msg.text}
              </div>
            ))}

            {(assistantThinking || loading) && (
              <div className="support-bubble ai-support typing-bubble">
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
              </div>
            )}

            {/* spacer so input bar doesn’t cover last bubble on iOS */}
            <div style={{ height: "4.75rem" }} />

            {/* single sentinel used by scrollToBottom */}
            <div ref={chatEndRef} />
          </div>

          {/* Completion banner (inside shell, above fixed input) */}
          {step === STEPS.DONE && (
            <div className="support-complete-banner">
              <div className="support-complete-inner">
                <div>
                  <strong>Ticket created successfully ✅</strong>
                  <div className="text-muted">
                    You’ll hear from us at <strong>{form.contact}</strong>.
                  </div>
                </div>
                <div className="support-complete-actions">
                  <Button size="sm" className="lpp-pill-btn" onClick={resetFlow}>
                    New ticket
                  </Button>
                  <Button
                    size="sm"
                    className="lpp-pill-btn-outline"
                    onClick={() => navigate("/")}
                  >
                    Back to home
                  </Button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* FIXED input bar */}
      <form
  className="chat-input-row support-input-row"
  onSubmit={(e) => {
    e.preventDefault();
    handleSend();
  }}
>
  {/* Honeypot stays the same */}
  <input
    type="text"
    style={{ display: "none" }}
    autoComplete="off"
    onChange={(e) => {
      if (e.target.value.length > 0) {
        setIsFlagged(true);
        addAiMessageWithDelay(
          "Your request was flagged. Please refresh the page and try again."
        );
      }
    }}
  />

  <div className="lpp-input-bar-container">
    <div className="chat-input-pill support-input-pill">
      <div className="chat-input-inner">
        {/* ✅ Voice icon button (same as other pages) */}
        <Button
          size="sm"
          className={`lpp-voice-btn ${voiceEnabled ? "is-on" : "is-off"}`}
          onClick={() => {
            const next = !voiceEnabled;
            setVoiceEnabled(next);

            if (!next) {
              stopVoice();
              voicePrimedRef.current = false;
              gesturePrimedOnceRef.current = false;
              lastSpokenAiIndexRef.current = -1;
              return;
            }

            voicePrimedRef.current = false;
            lastSpokenAiIndexRef.current = -1;

            // prime on click gesture
            primeVoice();
          }}
          title="Toggle voice"
          aria-label={voiceEnabled ? "Voice on" : "Voice off"}
          type="button"
        >
          <FontAwesomeIcon icon={voiceEnabled ? faVolumeHigh : faVolumeXmark} />
        </Button>

        <input
          className="chat-input-field support-input-field form-control"
          placeholder={
            step === STEPS.DONE
              ? "Ticket created. Use the buttons above to continue."
              : "Type your message…"
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading || isFlagged || assistantThinking || step === STEPS.DONE}
          onFocus={() => {
            if (voiceEnabled && !voicePrimedRef.current) {
              primeVoice();
              setTimeout(() => speakPendingAiMessages(), 300);
            }
          }}
          onTouchStart={() => {
            if (voiceEnabled && !voicePrimedRef.current) {
              primeVoice();
              setTimeout(() => speakPendingAiMessages(), 300);
            }
          }}
        />

        <button
          className="chat-send-icon-btn support-send-btn"
          disabled={loading || isFlagged || assistantThinking || step === STEPS.DONE}
          type="submit"
        >
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
      </div>
    </div>
  </div>
</form>

    </>
  );
}
